import { safeParseDate } from "@/lib/utils/date/date";
import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import { studioTodayYmd } from "@/lib/utils/date/studio-clock";
import {
  EnrollmentStatus,
  type GetEnrollmentResponse,
} from "@/openapi/generated";

type EnrollmentDroppedAt = { droppedAt?: unknown };

function isSentinelEnrollmentDateYmd(ymd: string): boolean {
  return ymd.startsWith("0000-") || ymd.startsWith("0001-");
}

function ymdFromParsedDate(parsed: Date): string | null {
  if (parsed.getFullYear() < 1900 && parsed.getUTCFullYear() < 1900) {
    return null;
  }
  const ymd = formatYmdLocal(parsed);
  return isSentinelEnrollmentDateYmd(ymd) ? null : ymd;
}

function parseEnrollmentDateYmd(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const ymd = trimmed.slice(0, 10);
      return isSentinelEnrollmentDateYmd(ymd) ? null : ymd;
    }
    const parsed = safeParseDate(trimmed);
    return parsed ? ymdFromParsedDate(parsed) : null;
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return ymdFromParsedDate(raw);
  }
  return null;
}

function isUtcDateOnlyClock(date: Date): boolean {
  if (
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0
  ) {
    return false;
  }
  const hours = date.getUTCHours();
  return hours === 0 || hours === 12;
}

/**
 * Instant for enrollment status events. Date-only / UTC midnight-or-noon
 * values become local calendar days so timezone does not shift the day.
 */
export function parseEnrollmentTimestamp(raw: unknown): Date | null {
  if (raw == null || raw === "") return null;

  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    if (raw.getFullYear() < 1900 && raw.getUTCFullYear() < 1900) return null;
    if (isUtcDateOnlyClock(raw)) {
      return new Date(
        raw.getUTCFullYear(),
        raw.getUTCMonth(),
        raw.getUTCDate(),
      );
    }
    return raw;
  }

  if (typeof raw !== "string") return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const ymd = trimmed.slice(0, 10);
    if (isSentinelEnrollmentDateYmd(ymd)) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = ymd.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
  }

  const parsed = safeParseDate(trimmed);
  if (!parsed) return null;
  if (parsed.getFullYear() < 1900 && parsed.getUTCFullYear() < 1900) {
    return null;
  }
  if (isUtcDateOnlyClock(parsed)) {
    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    );
  }
  return parsed;
}

/** Scheduled drop date from API `droppedAt` (BE uses Dropped + future droppedAt). */
export function getEnrollmentDroppedAtYmd(
  enrollment: EnrollmentDroppedAt,
): string | null {
  return parseEnrollmentDateYmd(enrollment.droppedAt);
}

/**
 * API may return null or "" for enrollments that are not dropped.
 * Future `droppedAt` means admin-scheduled withdrawal - still active until that date.
 */
export function isActiveEnrollment(enrollment: EnrollmentDroppedAt): boolean {
  const droppedAtYmd = getEnrollmentDroppedAtYmd(enrollment);
  if (droppedAtYmd == null) return true;
  return !isDropDateOnOrBeforeToday(droppedAtYmd);
}

export function isPendingEnrollment(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  return (
    enrollmentHasStatus(enrollment, EnrollmentStatus.Pending) &&
    isActiveEnrollment(enrollment)
  );
}

/** Full-class waitlist. BE stamps `Awaiting` when the class is full and has a waitlist. */
export function isWaitlistEnrollment(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  return (
    enrollmentHasStatus(enrollment, EnrollmentStatus.Awaiting) &&
    isActiveEnrollment(enrollment)
  );
}

/** Admin scheduled withdrawal: BE returns Dropped with a future droppedAt. */
export function isBackendFutureScheduledDrop(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  if (!enrollmentHasStatus(enrollment, EnrollmentStatus.Dropped)) return false;
  const droppedAtYmd = getEnrollmentDroppedAtYmd(enrollment);
  return droppedAtYmd != null && !isDropDateOnOrBeforeToday(droppedAtYmd);
}

/** Enrollment occupies a class spot (registration, capacity, attendance roster). */
export function occupiesClassSpot(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  if (!isActiveEnrollment(enrollment)) return false;
  const status = normalizeEnrollmentStatus(enrollment.status);
  if (isBackendFutureScheduledDrop(enrollment)) return true;
  return (
    status === EnrollmentStatus.Enrolled ||
    status === EnrollmentStatus.Pending ||
    status === EnrollmentStatus.Awaiting ||
    status === EnrollmentStatus.ScheduledDrop
  );
}

/** Seat-holding roster row. Waitlist (`Awaiting`) does not occupy a spot. */
export function isOccupyingClassRosterSpot(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  return occupiesClassSpot(enrollment) && !isWaitlistEnrollment(enrollment);
}

function enrollmentStudentId(
  enrollment: Pick<GetEnrollmentResponse, "student"> & {
    studentId?: unknown;
  },
): number | null {
  const nested = Number(enrollment.student?.studentId);
  if (Number.isFinite(nested) && nested > 0) return nested;
  const root = Number(enrollment.studentId);
  if (Number.isFinite(root) && root > 0) return root;
  return null;
}

export function countOccupyingStudentsByClassId(
  enrollments: readonly (Pick<
    GetEnrollmentResponse,
    "status" | "droppedAt" | "_class" | "student"
  > & { studentId?: unknown })[],
): Map<number, number> {
  const studentsByClassId = new Map<number, Set<number>>();
  const anonymousByClassId = new Map<number, number>();

  for (const enrollment of enrollments) {
    const classId = Number(enrollment._class?.classId);
    if (!Number.isFinite(classId) || classId <= 0) continue;
    if (!studentsByClassId.has(classId))
      studentsByClassId.set(classId, new Set());
    if (!isOccupyingClassRosterSpot(enrollment)) continue;

    const studentId = enrollmentStudentId(enrollment);
    if (studentId == null) {
      anonymousByClassId.set(
        classId,
        (anonymousByClassId.get(classId) ?? 0) + 1,
      );
      continue;
    }

    studentsByClassId.get(classId)?.add(studentId);
  }

  const counts = new Map<number, number>();
  for (const [classId, studentIds] of studentsByClassId) {
    counts.set(
      classId,
      studentIds.size + (anonymousByClassId.get(classId) ?? 0),
    );
  }

  return counts;
}

/** Classes that currently have at least one student waiting. */
export function classIdsWithActiveWaitlist(
  enrollments: readonly (Pick<
    GetEnrollmentResponse,
    "status" | "droppedAt" | "_class"
  >)[],
): Set<number> {
  const ids = new Set<number>();
  for (const enrollment of enrollments) {
    if (!isWaitlistEnrollment(enrollment)) continue;
    const classId = Number(enrollment._class?.classId);
    if (Number.isFinite(classId) && classId > 0) ids.add(classId);
  }
  return ids;
}

/**
 * Unique students currently in at least one class.
 * A student in three classes still counts as one. Waitlist-only and
 * pending-review-only enrollments do not count; scheduled drops still do.
 */
export function countUniqueCurrentlyEnrolledStudents(
  enrollments: readonly (Pick<
    GetEnrollmentResponse,
    "status" | "droppedAt" | "student"
  > & {
    studentId?: unknown;
  })[],
): number {
  const studentIds = new Set<number>();

  for (const enrollment of enrollments) {
    if (!isOccupyingClassRosterSpot(enrollment)) continue;
    if (isPendingEnrollment(enrollment)) continue;
    const studentId = enrollmentStudentId(enrollment);
    if (studentId == null) continue;
    studentIds.add(studentId);
  }

  return studentIds.size;
}

/**
 * Student is currently in the class (tuition strip, enrolled count).
 * Includes future withdraws. Excludes waitlist Pending.
 */
export function isCurrentClassEnrollment(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  return occupiesClassSpot(enrollment) && !isPendingEnrollment(enrollment);
}

/** Admin students list CLASSES column — includes scheduled withdraws. */
export function countStudentListClasses(
  enrollments: readonly Pick<GetEnrollmentResponse, "status" | "droppedAt">[],
): number {
  return enrollments.filter(occupiesClassSpot).length;
}

/** Normalize API status strings (PascalCase, camelCase, etc.). */
export function normalizeEnrollmentStatus(
  status: unknown,
): EnrollmentStatus | null {
  if (status == null || status === "") return null;
  const raw = String(status).trim();
  const direct = Object.values(EnrollmentStatus).find(
    (s) => s.toLowerCase() === raw.toLowerCase(),
  );
  return direct ?? null;
}

export function enrollmentHasStatus(
  enrollment: Pick<GetEnrollmentResponse, "status">,
  expected: EnrollmentStatus,
): boolean {
  return normalizeEnrollmentStatus(enrollment.status) === expected;
}

/** Member requested withdrawal; awaiting admin approve/reject (`ScheduledDrop` status). */
export function isScheduledDropPending(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): boolean {
  return (
    enrollmentHasStatus(enrollment, EnrollmentStatus.ScheduledDrop) &&
    isActiveEnrollment(enrollment)
  );
}

export type EnrollmentDropScheduleFields = Pick<
  GetEnrollmentResponse,
  "status" | "droppedAt"
> &
  Partial<Pick<GetEnrollmentResponse, "dropDate">>;

/** Admin scheduled withdrawal on a future date. */
export function hasAdminScheduledFutureDrop(
  enrollment: EnrollmentDropScheduleFields,
): boolean {
  if (isBackendFutureScheduledDrop(enrollment)) return true;
  const dropDateYmd = getEnrollmentDropDate(enrollment);
  if (dropDateYmd && !isDropDateOnOrBeforeToday(dropDateYmd)) {
    return true;
  }
  if (!isActiveEnrollment(enrollment)) return false;
  if (!enrollmentHasStatus(enrollment, EnrollmentStatus.Enrolled)) return false;
  return false;
}

/** Member request pending approval or admin future drop - either can be canceled. */
export function canCancelScheduledDrop(
  enrollment: EnrollmentDropScheduleFields,
): boolean {
  return (
    isScheduledDropPending(enrollment) ||
    hasAdminScheduledFutureDrop(enrollment)
  );
}

export function canRequestScheduledDrop(
  enrollment: EnrollmentDropScheduleFields,
): boolean {
  return (
    enrollmentHasStatus(enrollment, EnrollmentStatus.Enrolled) &&
    isActiveEnrollment(enrollment) &&
    !isScheduledDropPending(enrollment) &&
    !hasAdminScheduledFutureDrop(enrollment)
  );
}

/** Admin can drop an actively enrolled student (today or future date). */
export const canAdminDropEnrollment = canRequestScheduledDrop;

/** Scheduled drop date from API `dropDate` when present. */
export function getEnrollmentDropDate(
  enrollment: Partial<Pick<GetEnrollmentResponse, "dropDate">>,
): string | null {
  return parseEnrollmentDateYmd(enrollment.dropDate);
}

/** Scheduled drop date from either `dropDate` or future `droppedAt`. */
export function getScheduledDropDateYmd(
  enrollment: EnrollmentDropScheduleFields,
): string | null {
  const fromDropDate = getEnrollmentDropDate(enrollment);
  if (fromDropDate) return fromDropDate;
  if (isBackendFutureScheduledDrop(enrollment)) {
    return getEnrollmentDroppedAtYmd(enrollment);
  }
  return null;
}

/** Future withdraw day from `dropDate` or `droppedAt`, regardless of status. */
export function getUpcomingWithdrawYmd(
  enrollment: EnrollmentDropScheduleFields,
): string | null {
  const dropDateYmd = getEnrollmentDropDate(enrollment);
  if (dropDateYmd && !isDropDateOnOrBeforeToday(dropDateYmd)) {
    return dropDateYmd;
  }
  const droppedYmd = getEnrollmentDroppedAtYmd(enrollment);
  if (droppedYmd && !isDropDateOnOrBeforeToday(droppedYmd)) {
    return droppedYmd;
  }
  return null;
}

export function formatEnrollmentDropDate(
  enrollment: EnrollmentDropScheduleFields,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
): string | null {
  const ymd = getScheduledDropDateYmd(enrollment);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return ymd;
  }
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).toLocaleDateString("en-US", {
    ...options,
    timeZone: "UTC",
  });
}

/** Label for admin-scheduled future withdrawal date. */
export function formatAdminScheduledDropLabel(
  enrollment: EnrollmentDropScheduleFields,
): string | null {
  if (!hasAdminScheduledFutureDrop(enrollment)) return null;
  return formatEnrollmentDropDate(enrollment);
}

/** When the drop was requested (API dropDate or enrollment updatedAt). */
export function formatEnrollmentDropRequestLabel(
  enrollment: EnrollmentDropScheduleFields &
    Partial<Pick<GetEnrollmentResponse, "updatedAt">>,
): string | null {
  if (!isScheduledDropPending(enrollment)) return null;
  const fromDropDate = formatEnrollmentDropDate(enrollment);
  if (fromDropDate) return fromDropDate;
  if (enrollment.updatedAt) {
    const d =
      enrollment.updatedAt instanceof Date
        ? enrollment.updatedAt
        : new Date(enrollment.updatedAt);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  }
  return null;
}

/** True when dropDate is today or in the past (studio calendar). */
export function isDropDateOnOrBeforeToday(dropDateYmd: string): boolean {
  return dropDateYmd <= studioTodayYmd();
}

/** Minimum selectable drop date for member requests (today, studio calendar). */
export function minScheduledDropDateYmd(): string {
  return studioTodayYmd();
}

export function isValidScheduledDropDateYmd(dropDateYmd: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dropDateYmd)) return false;
  const [y, m, d] = dropDateYmd.split("-").map(Number);
  const candidate = new Date(y, m - 1, d);
  if (
    candidate.getFullYear() !== y ||
    candidate.getMonth() !== m - 1 ||
    candidate.getDate() !== d
  ) {
    return false;
  }
  return dropDateYmd >= minScheduledDropDateYmd();
}
